import React, { useEffect, useMemo, useState } from 'react';
import { Select, Spin, Input } from 'antd';
import { DrawerItem } from '../Legend/LegendDrawer';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import ViewAttributeTable from '../Legend/ViewAttributeTable';
import { useLayerProperitesContext, defaultLayerPropertiesState } from '../../../context/LayerPropertiesContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useQueryExtentDraw, ExtentPolygon } from '../../../hooks/useQueryExtentDraw';
import { Filter, FilterItem, NULL_VALUE, SELECT_CARET } from './QueryToolModal';
import './QueryToolModal.scss';
import * as Cesium from 'cesium';
import { formatDisplayDate } from '../../../shared/utils/formatDisplayDate';
import { useAuth } from '../../../context/AuthContext';
import { MapToolAction } from '../../../enums/mapPortalActions';
import { mapToolAccess } from '../constants';

const { Option } = Select;

type SearchExtent = 'all' | 'polygon';
type Relationship = '' | 'within' | 'outside' | 'closer' | 'farther';

// UI label -> backend relationship. Touch is intentionally omitted (US-103032
// Scenario 11 was dropped — "Are Within" already uses ST_Intersects).
const RELATIONSHIPS: { value: Exclude<Relationship, ''>; label: string }[] = [
  { value: 'within', label: 'ARE WITHIN' },
  { value: 'outside', label: 'ARE OUTSIDE' },
  { value: 'closer', label: 'CLOSER THAN' },
  { value: 'farther', label: 'FARTHER THAN' },
];
const UNITS = ['meters', 'kilometers', 'miles', 'feet'] as const;
const isDistanceRel = (r: Relationship) => r === 'closer' || r === 'farther';

// Helper to escape single quotes for CQL
const lit = (v: any) => (typeof v === 'number' ? v : `'${String(v).replace(/'/g, "''")}'`);
const like = (v: any) => `'%${String(v).replace(/'/g, "''")}%'`;

/**
 * Builds the complex CQL string for spatial joins (Predicate Highlight).
 * Requires gs-query-layer extension.
 */
const buildProximityCql = (
  sourceFilters: Filter[],
  relationship: Relationship,
  distance: string,
  unit: string,
  targetLayer: string,
  targetFilter: Filter,
  extent: ExtentPolygon | null,
): string => {
  const workspace = 'mapaki';

  const getFilterPart = (f: Filter) => {
    if (!f.category || !f.condition) return null;
    const isNullValue = f.value === NULL_VALUE;
    switch (f.condition) {
      case 'IS':
        return isNullValue ? `${f.category} IS NULL` : `${f.category} = ${lit(f.value)}`;
      case 'IS NOT':
        return isNullValue ? `${f.category} IS NOT NULL` : `${f.category} <> ${lit(f.value)}`;
      case 'LIKE':
        return `${f.category} ILIKE ${like(f.value)}`;
      case 'NOT LIKE':
        return `NOT (${f.category} ILIKE ${like(f.value)})`;
      case 'GT':
        return `${f.category} > ${lit(f.value)}`;
      case 'GTE':
        return `${f.category} >= ${lit(f.value)}`;
      case 'LT':
        return `${f.category} < ${lit(f.value)}`;
      case 'LTE':
        return `${f.category} <= ${lit(f.value)}`;
      case 'EMPTY':
        return `${f.category} IS NULL`;
      case 'NOT_EMPTY':
        return `${f.category} IS NOT NULL`;
      default:
        return null;
    }
  };

  // 1. Source Attribute Conditions
  let sourceAttrCql = '';
  sourceFilters.forEach((f, i) => {
    const part = getFilterPart(f);
    if (!part) return;
    if (i === 0) sourceAttrCql = part;
    else {
      const op = f.logicalOperator?.toUpperCase() === 'OR' ? 'OR' : 'AND';
      sourceAttrCql = `(${sourceAttrCql}) ${op} (${part})`;
    }
  });

  // 2. Target Layer Logic (Subquery)
  const tFilterPart = getFilterPart(targetFilter);
  const targetCql = tFilterPart || 'INCLUDE';
  const targetGeom = `collectGeometries(queryCollection('${workspace}:${targetLayer}', 'geom', '${targetCql.replace(/'/g, "''")}'))`;

  // 3. Spatial Relationship
  let spatialCql = '';
  if (relationship === 'within') spatialCql = `INTERSECTS(geom, ${targetGeom})`;
  else if (relationship === 'outside') spatialCql = `DISJOINT(geom, ${targetGeom})`;
  else if (relationship === 'closer') spatialCql = `DWITHIN(geom, ${targetGeom}, ${distance || 0}, ${unit})`;
  else if (relationship === 'farther') spatialCql = `NOT DWITHIN(geom, ${targetGeom}, ${distance || 0}, ${unit})`;

  // 4. Polygon Extent
  let extentCql = '';
  if (extent) {
    const ring = extent.coordinates[0];
    const wkt = `POLYGON((${ring.map((p) => `${p[0]} ${p[1]}`).join(', ')}))`;
    extentCql = `INTERSECTS(geom, ${wkt})`;
  }

  const finalParts = [sourceAttrCql, spatialCql, extentCql].filter(Boolean).map((p) => `(${p})`);
  return finalParts.join(' AND ');
};

// Build the left-associative sift query from filter rows (mirrors Query Tool).
// NULL_VALUE from the shared FilterItem dropdown must unwrap to real null so
// the backend emits `IS NULL` / `IS NOT NULL` instead of comparing to '__NULL__'.
const buildSiftQuery = (filters: Filter[]): Record<string, any> => {
  const escapeRegexSift = (v: string) => String(v).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const toConditionSift = (category: string, condition: string, value: string): Record<string, any> => {
    const isNullValue = value === NULL_VALUE;
    const isNumeric = !isNullValue && !isNaN(parseFloat(value)) && isFinite(Number(value));
    const val = isNullValue ? null : isNumeric ? Number(value) : value;
    switch (condition) {
      case 'IS':
        return { [category]: val };
      case 'IS NOT':
        return { [category]: { $ne: val } };
      case 'LIKE':
        return { [category]: { $regex: escapeRegexSift(value), $options: 'i' } };
      case 'NOT LIKE':
        return { [category]: { $not: { $regex: escapeRegexSift(value), $options: 'i' } } };
      case 'EMPTY':
        return { [category]: null };
      case 'NOT_EMPTY':
        return { [category]: { $ne: null } };
      default:
        return {};
    }
  };

  return filters.reduce((acc: Record<string, any>, f: Filter, i: number) => {
    if (!f.category || !f.condition) return acc;
    const needsValue = f.condition !== 'EMPTY' && f.condition !== 'NOT_EMPTY';
    // NULL_VALUE is a non-empty sentinel string, so it passes the empty check.
    if (needsValue && (f.value === undefined || f.value === null || f.value === '')) return acc;
    const cond = toConditionSift(f.category, f.condition, f.value);
    if (i === 0 || !Object.keys(acc).length) return cond;
    const op: '$and' | '$or' = f.logicalOperator?.toLowerCase() === 'or' ? '$or' : '$and';
    return { [op]: [acc, cond] };
  }, {});
};

interface ProximityModalProps {
  proximityModalToolVisible: boolean;
  setProximityModalToolVisible: (value: boolean) => void;
  drawerItems: DrawerItem[];
  setDrawerItems: React.Dispatch<React.SetStateAction<any[]>>;
  settings?: {
    queryTool: { enabled: boolean; name: string; color: string };
    proximityTool: { enabled: boolean; name: string; color: string };
  };
  mapId?: string;
  viewerRef: any;
  setCurrentTool?: (value: string) => void;
  setIsLayerLoading?: (loading: boolean) => void;
  onLayerUpdateComplete?: () => Promise<void>;
}

const ProximityModal: React.FC<ProximityModalProps> = ({
  proximityModalToolVisible,
  setProximityModalToolVisible,
  drawerItems,
  setDrawerItems,
  settings,
  mapId,
  viewerRef,
  setCurrentTool,
  setIsLayerLoading,
  onLayerUpdateComplete,
}) => {
  const { canAccess } = useAuth();
  const canSaveResults = canAccess(mapToolAccess(MapToolAction.SAVE_TABLE_RESULTS));

  // Search extent (US-102858, identical to the Query Tool).
  const [searchExtent, setSearchExtent] = useState<SearchExtent>('all');
  const [extentPolygon, setExtentPolygon] = useState<ExtentPolygon | null>(null);
  const [extentLayerNames, setExtentLayerNames] = useState<string[] | null>(null);

  // FIND IN LAYER (source) + its WHERE rows.
  const [sourceLayer, setSourceLayer] = useState('');
  const [sourceFilters, setSourceFilters] = useState<Filter[]>([{ category: '', condition: '', value: '', logicalOperator: '' }]);

  // THAT ARE (relationship) + distance.
  const [relationship, setRelationship] = useState<Relationship>('');
  const [distance, setDistance] = useState('');
  const [unit, setUnit] = useState<(typeof UNITS)[number]>('meters');

  // LAYER (comparison/target) + its optional WHERE row.
  const [targetLayer, setTargetLayer] = useState('');
  const [targetFilter, setTargetFilter] = useState<Filter>({ category: '', condition: '', value: '', logicalOperator: '' });

  // Field/value maps per layer + validation/errors.
  const [sourceFeatures, setSourceFeatures] = useState<Record<string, any[]>>({});
  const [targetFeatures, setTargetFeatures] = useState<Record<string, any[]>>({});
  const [errors, setErrors] = useState<{ source?: string; target?: string; distance?: string; rows?: Record<number, string> }>({});
  const [submitError, setSubmitError] = useState('');

  // Results — first page of match ids only; total comes from the paginated API.
  const [resultVisible, setResultVisible] = useState(false);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [matchTotal, setMatchTotal] = useState(0);
  // The config that produced the current results — drives the result table's
  // match-first ordering.
  const [activeProximity, setActiveProximity] = useState<Record<string, any> | null>(null);
  const seedSelectionIds = useMemo(() => (filteredItems ?? []).map((f: any) => f.id).filter(Boolean), [filteredItems]);
  const seedSelectAllMatches = matchTotal > seedSelectionIds.length;

  const [activeQuery, setActiveQuery] = useState<{
    filter: Record<string, any>;
    extent: ExtentPolygon | null;
    cql: string;
  } | null>(null);

  const { fetchFeaturesKeyAndValueMapping, proximityFeatures, fetchLayersInExtent, getLayerBounds, getLayerSymbologyFields } = MapPortalService();
  const { mutateAsync: runProximity, isPending } = useMutation(proximityFeatures());
  const { mutateAsync: getLayerBoundsAsync } = useMutation(getLayerBounds());

  const { data: sourceKeyResponse, isFetching: isSourceKeyFetching } = useQuery(fetchFeaturesKeyAndValueMapping(sourceLayer));
  const { data: targetKeyResponse, isFetching: isTargetKeyFetching } = useQuery(fetchFeaturesKeyAndValueMapping(targetLayer));
  // Field schema (name + numeric flag) so FilterItem can expose GT/LT/etc. for
  // Integer/Float columns — including ones just added via the attribute table.
  const { data: sourceFieldsResponse } = useQuery(getLayerSymbologyFields(sourceLayer));
  const sourceLayerSchema = useMemo(() => sourceFieldsResponse?.fields || [], [sourceFieldsResponse]);
  const { setState } = useLayerProperitesContext();
  const { mutateAsync: getLayersInExtent, isPending: extentLayersLoading } = useMutation(fetchLayersInExtent());

  const { isDrawing, beginDraw, cancelDraw, clearExtent } = useQueryExtentDraw({
    viewerRef,
    // onComplete: (polygon) => setExtentPolygon(polygon),
    onComplete: async (polygon) => {
      setExtentPolygon(polygon);
      // Narrow the WITHIN dropdown to layers that have data inside the polygon.
      try {
        const names = await getLayersInExtent({
          layerNames: drawerItems.map((l: any) => l.uniqueLayerName),
          extent: polygon,
        });
        setExtentLayerNames(names);
        // If the selected layer no longer has data in the extent, reset it.
        if (sourceLayer && !names.includes(sourceLayer)) {
          setSourceLayer('');
          setSourceFilters([{ category: '', condition: '', value: '', logicalOperator: '' }]);
          setRelationship('');
          setTargetLayer('');
          setTargetFilter({ category: '', condition: '', value: '', logicalOperator: '' });
          setTargetFeatures({});
        }
      } catch {
        // On failure, leave the dropdown unfiltered rather than blocking.
        setExtentLayerNames(null);
      }
    },

    onCancel: () => {
      setExtentPolygon(null);
      setExtentLayerNames(null);
      setSearchExtent('all');
    },
  });

  useEffect(() => {
    if (sourceKeyResponse) setSourceFeatures(sourceKeyResponse);
  }, [sourceKeyResponse]);
  useEffect(() => {
    if (targetKeyResponse) setTargetFeatures(targetKeyResponse);
  }, [targetKeyResponse]);

  // Clear stale errors as the user edits.
  useEffect(() => {
    setErrors({});
    setSubmitError('');
  }, [sourceLayer, targetLayer, sourceFilters, relationship, distance, targetFilter]);

  const sourceKeyOptions = useMemo(() => Object.keys(sourceFeatures).map((k) => ({ label: k, value: k })), [sourceFeatures]);
  const targetKeyOptions = useMemo(() => Object.keys(targetFeatures).map((k) => ({ label: k, value: k })), [targetFeatures]);
  const layerTitleOf = (name: string) => drawerItems.find((l: any) => l.uniqueLayerName === name)?.layerTitle;

  // --- Extent toggle ---
  const handleSelectAll = () => {
    setSearchExtent('all');
    cancelDraw();
    clearExtent();
    setExtentPolygon(null);
    setExtentLayerNames(null);
  };
  const handleSelectPolygon = () => {
    setSearchExtent('polygon');
    clearExtent();
    setExtentPolygon(null);
    setExtentLayerNames(null);
    setCurrentTool?.('');
    beginDraw();
  };

  const withinLayers =
    searchExtent === 'polygon' && extentPolygon && extentLayerNames
      ? drawerItems.filter((l: any) => extentLayerNames.includes(l.uniqueLayerName))
      : drawerItems;

  // --- Source WHERE rows ---
  const updateRow = (index: number, patch: Partial<Filter>) => {
    setSourceFilters((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };
  const handleAddFilter = () => setSourceFilters((prev) => [...prev, { category: '', condition: '', value: '', logicalOperator: '' }]);
  const handleRemoveFilter = (index: number) => setSourceFilters((prev) => prev.filter((_, i) => i !== index));
  const handleCategoryChange = (value: string, index: number) => updateRow(index, { category: value, condition: '', value: '' });
  const handleConditionChange = (value: string, index: number) => updateRow(index, { condition: value });
  const handleValueChange = (value: string, index: number) => updateRow(index, { value });
  const handleLogicalOperatorChange = (value: string, index: number) => updateRow(index, { logicalOperator: value });

  // --- Optional target WHERE row ---
  const handleTargetCategory = (value: string) => setTargetFilter((f) => ({ ...f, category: value, condition: '', value: '' }));
  const handleTargetCondition = (value: string) => setTargetFilter((f) => ({ ...f, condition: value }));
  const handleTargetValue = (value: string) => setTargetFilter((f) => ({ ...f, value }));

  const handleSourceLayerChange = (value: string) => {
    setSourceLayer(value);
    // New layer => its fields differ; reset the source conditions.
    setSourceFilters([{ category: '', condition: '', value: '', logicalOperator: '' }]);
    setSourceFeatures({});
    const layer = drawerItems.find((l: any) => l.uniqueLayerName === value);
    if (layer) {
      setState(() => ({
        ...defaultLayerPropertiesState,
        ...Object.fromEntries(Object.entries(layer).filter(([_, v]) => v !== null && v !== undefined)),
        isExistingLayer: true,
      }));
    }
  };
  const handleTargetLayerChange = (value: string) => {
    setTargetLayer(value);
    setTargetFilter({ category: '', condition: '', value: '', logicalOperator: '' });
    setTargetFeatures({});
  };

  const validate = (): boolean => {
    if (!sourceLayer) {
      setErrors({ source: 'Please select a layer to analyze.' });
      return false;
    }
    if (!relationship) {
      setErrors({ source: 'Please select a spatial relationship.' });
      return false;
    }
    if (isDistanceRel(relationship)) {
      if (distance.trim() === '') {
        setErrors({ distance: 'Please enter a distance value.' });
        return false;
      }
      if (!Number.isFinite(Number(distance)) || Number(distance) <= 0) {
        setErrors({ distance: 'Please enter a valid distance.' });
        return false;
      }
    }
    if (!targetLayer) {
      setErrors({ target: 'Please select a comparison layer.' });
      return false;
    }
    return true;
  };

  const clearHighlight = () => {
    setState((prev: any) => ({ ...prev, selectedFeatureIds: [], selectionCql: null, highlightColor: null }));
    setActiveProximity(null);
    setActiveQuery(null);
  };

  const handleApply = async () => {
    setSubmitError('');
    if (!validate()) return;
    // Scenario 28: a selected layer may have been removed.
    const present = (n: string) => drawerItems.some((l: any) => l.uniqueLayerName === n);
    if (!present(sourceLayer) || !present(targetLayer)) {
      setSubmitError('Selected layer is no longer available.');
      return;
    }

    try {
      const sourceSiftFilter = buildSiftQuery(sourceFilters);
      const targetSiftFilter = buildSiftQuery([targetFilter]);

      const payload: any = {
        sourceLayer,
        targetLayer,
        relationship: relationship as any,
        extent: searchExtent === 'polygon' ? extentPolygon : null,
        // Page 0 only — full match set is never materialised client-side.
        page: 0,
        pageSize: 500,
      };

      if (Object.keys(sourceSiftFilter).length) payload.sourceFilter = sourceSiftFilter;
      if (Object.keys(targetSiftFilter).length) payload.targetFilter = targetSiftFilter;
      if (isDistanceRel(relationship)) {
        payload.distance = Number(distance);
        payload.unit = unit;
      }

      const result = await runProximity(payload);
      // Backward-compat: older API may still return a bare feature array.
      const features = Array.isArray(result) ? result : (result?.features ?? []);
      const total = Array.isArray(result) ? result.length : Number(result?.total ?? 0);

      // Build logic-based CQL highlight
      const proximityCql = buildProximityCql(sourceFilters, relationship, distance, unit, targetLayer, targetFilter, extentPolygon);

      // Trigger the map highlight via logic
      setState((prev) => ({
        ...prev,
        uniqueLayerName: sourceLayer,
        selectedFeatureIds: [],
        selectionCql: proximityCql,
        highlightColor: settings?.proximityTool?.color ?? null,
      }));

      // Set activeQuery to drive Attribute Table server-side sort (Matches on Top)
      setActiveQuery({
        filter: sourceSiftFilter,
        extent: searchExtent === 'polygon' ? extentPolygon : null,
        cql: proximityCql,
      });

      // Maintain activeProximity for legacy reference + export/save match predicate
      setActiveProximity({
        relationship,
        targetLayer,
        ...(isDistanceRel(relationship) ? { distance: Number(distance), unit } : {}),
        ...(Object.keys(targetSiftFilter).length ? { targetFilter: targetSiftFilter } : {}),
        ...(Object.keys(sourceSiftFilter).length ? { sourceFilter: sourceSiftFilter } : {}),
        ...(searchExtent === 'polygon' ? { extent: extentPolygon } : {}),
      });

      setFilteredItems(features);
      setMatchTotal(total);
      setResultVisible(true);
      setProximityModalToolVisible(false);
      setTimeout(async () => {
        try {
          const bounds = await getLayerBoundsAsync({ layerName: sourceLayer });
          if (bounds && viewerRef.current) {
            const minX = parseFloat(bounds.minX || bounds.minx);
            const minY = parseFloat(bounds.minY || bounds.miny);
            const maxX = parseFloat(bounds.maxX || bounds.maxx);
            const maxY = parseFloat(bounds.maxY || bounds.maxy);

            if (!isNaN(minX) && (minX !== 0 || minY !== 0)) {
              viewerRef.current.camera.flyTo({
                destination: Cesium.Rectangle.fromDegrees(minX, minY, maxX, maxY),
                duration: 1.5,
              });
            }
          }
        } catch (boundsErr) {
          console.warn('Auto-zoom failed:', boundsErr);
        }
      }, 1000);
    } catch (err: any) {
      setSubmitError('Unable to process proximity analysis. Please try again.');
    }
  };

  // Reset the entire proximity configuration back to a fresh state (source/target
  // layers, filters, relationship/distance, extent, errors, results and the map
  // highlight). Shared by the result table's Reset Query action and by closing
  // the panel, so re-opening always starts fresh.
  const resetConfig = () => {
    setSearchExtent('all');
    setExtentPolygon(null);
    setExtentLayerNames(null);
    setSourceLayer('');
    setSourceFilters([{ category: '', condition: '', value: '', logicalOperator: '' }]);
    setRelationship('');
    setDistance('');
    setUnit('meters');
    setTargetLayer('');
    setTargetFilter({ category: '', condition: '', value: '', logicalOperator: '' });
    setSourceFeatures({});
    setTargetFeatures({});
    setErrors({});
    setSubmitError('');
    setFilteredItems([]);
    setMatchTotal(0);
    cancelDraw();
    clearExtent();
    clearHighlight();
  };

  const handleCancel = () => {
    if (isPending) return; // Closing the panel discards the whole configuration so re-opening the tool
    // starts fresh (no retained layers/filters).
    resetConfig();
    setProximityModalToolVisible(false);
  };

  // Reset Query (result table): clear the whole configuration and return to the
  // Proximity tool modal — mirrors the Query Tool's handleClear.
  const handleReset = () => {
    resetConfig();
    setSearchExtent('all');
    setExtentPolygon(null);
    setExtentLayerNames(null);
    setSourceLayer('');
    setSourceFilters([{ category: '', condition: '', value: '', logicalOperator: '' }]);
    setRelationship('');
    setDistance('');
    setUnit('meters');
    setTargetLayer('');
    setTargetFilter({ category: '', condition: '', value: '', logicalOperator: '' });
    setSourceFeatures({});
    setTargetFeatures({});
    setErrors({});
    setSubmitError('');
    setFilteredItems([]);
    cancelDraw();
    clearExtent();
    clearHighlight();
    setResultVisible(false);
    setProximityModalToolVisible(true);
  };

  return (
    <>
      {isDrawing && (
        <div className="query-tool__draw-banner">
          Click the starting point to finish drawing.
          <span className="query-tool__draw-banner-hint">Right-click to cancel.</span>
        </div>
      )}

      {proximityModalToolVisible && (
        <div className={`query-panel query-panel--proximity ${isPending ? 'query-panel--disable' : ''}`}>
          <div className="query-panel__head">
            <span className="query-panel__title">{settings?.proximityTool?.name?.trim() || 'Proximity Tool'}</span>
            <button type="button" aria-label="Close" className="query-panel__close" onClick={handleCancel}>
              ×
            </button>
          </div>

          {/* US-102858 search extent: All vs Polygon. */}
          <div className="query-tool__extent">
            <button type="button" className={`query-tool__extent-btn ${searchExtent === 'all' ? 'is-active' : ''}`} onClick={handleSelectAll}>
              ALL
            </button>
            <button
              type="button"
              aria-label="Draw polygon search extent"
              className={`query-tool__extent-btn query-tool__extent-btn--icon ${searchExtent === 'polygon' ? 'is-active' : ''}`}
              onClick={handleSelectPolygon}
            >
              <svg width="23" height="18" viewBox="0 0 23 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M3.91708 1.54953L8.69584 5.08935L21.7932 1.01855V8.80617L12.9436 16.7708L0.908234 12.346L3.91708 1.54953Z"
                  stroke={searchExtent === 'polygon' ? '#ffffff' : '#333333'}
                  stroke-width="2"
                  stroke-miterlimit="10"
                />
              </svg>
            </button>
          </div>

          {/* FIND IN LAYER (primary) */}
          <div className="query-panel__row">
            <span className="query-tool__label">FIND IN LAYER</span>
            <Select
              suffixIcon={SELECT_CARET}
              popupClassName="query-tool__dropdown"
              placeholder="select layer"
              className="query-tool__within-select"
              loading={extentLayersLoading}
              value={sourceLayer || undefined}
              onChange={handleSourceLayerChange}
              showSearch
              popupMatchSelectWidth={false}
            >
              {withinLayers?.map((layer: any, index: number) => (
                <Option key={index} value={layer.uniqueLayerName}>
                  {layer.layerTitle || layer.uniqueLayerName}
                </Option>
              ))}
            </Select>
          </div>
          {errors.source && <div className="query-tool__error query-tool__error--layer">{errors.source}</div>}

          {/* WHERE — source attribute filters */}
          {sourceFilters.map((filter, index) => (
            <FilterItem
              key={index}
              filter={filter}
              index={index}
              keyOptions={sourceKeyOptions}
              features={sourceFeatures}
              layerName={sourceLayer}
              error={errors.rows?.[index]}
              handleCategoryChange={handleCategoryChange}
              handleConditionChange={handleConditionChange}
              handleValueChange={handleValueChange}
              handleLogicalOperatorChange={handleLogicalOperatorChange}
              handleRemoveFilter={handleRemoveFilter}
              setDrawerItems={setDrawerItems}
              handleFeatureChange={handleValueChange}
              schema={sourceLayerSchema}
              loading={isSourceKeyFetching}
              optionsLoading={isSourceKeyFetching}
            />
          ))}

          <div className="query-panel__add-wrap">
            <button type="button" className="query-panel__add" onClick={handleAddFilter}>
              ADD
            </button>
          </div>

          {/* THAT ARE — spatial relationship (+ distance/unit when applicable) */}
          <div className="query-panel__row">
            <span className="query-tool__label">THAT ARE</span>
            <Select
              suffixIcon={SELECT_CARET}
              popupClassName="query-tool__dropdown"
              placeholder="relationship"
              className="query-tool__field proximity__relationship"
              value={relationship || undefined}
              onChange={(v) => setRelationship(v)}
              popupMatchSelectWidth={false}
            >
              {RELATIONSHIPS.map((r) => (
                <Option key={r.value} value={r.value}>
                  {r.label}
                </Option>
              ))}
            </Select>
            {isDistanceRel(relationship) && (
              <>
                <Input
                  className="query-tool__field proximity__distance"
                  placeholder="distance"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                  inputMode="decimal"
                />
                <Select
                  suffixIcon={SELECT_CARET}
                  popupClassName="query-tool__dropdown"
                  className="query-tool__field"
                  value={unit}
                  onChange={(v) => setUnit(v)}
                  popupMatchSelectWidth={false}
                >
                  {UNITS.map((u) => (
                    <Option key={u} value={u}>
                      {u.toUpperCase()} FROM
                    </Option>
                  ))}
                </Select>
              </>
            )}
          </div>
          {errors.distance && <div className="query-tool__error query-tool__error--layer">{errors.distance}</div>}

          {/* LAYER — comparison layer (comes last, per Scenario 3) */}
          <div className="query-panel__row">
            <span className="query-tool__label">LAYER</span>
            <Select
              suffixIcon={SELECT_CARET}
              popupClassName="query-tool__dropdown"
              placeholder="select layer"
              className="query-tool__within-select"
              value={targetLayer || undefined}
              onChange={handleTargetLayerChange}
              showSearch
              popupMatchSelectWidth={false}
            >
              {drawerItems?.map((layer: any, index: number) => (
                <Option key={index} value={layer.uniqueLayerName}>
                  {layer.layerTitle || layer.uniqueLayerName}
                </Option>
              ))}
            </Select>
          </div>
          {errors.target && <div className="query-tool__error query-tool__error--layer">{errors.target}</div>}

          {/* WHERE (OPTIONAL) — comparison layer filter (Scenario 17*) */}
          {targetLayer && (
            <div className="query-panel__row">
              <span className="query-tool__label">WHERE (OPTIONAL)</span>
              <Select
                suffixIcon={SELECT_CARET}
                popupClassName="query-tool__dropdown"
                className="query-tool__field"
                placeholder="Category"
                value={targetFilter.category || undefined}
                onChange={handleTargetCategory}
                allowClear
                popupMatchSelectWidth={false}
                loading={isTargetKeyFetching}
              >
                {targetKeyOptions.map((k) => (
                  <Option key={k.value} value={k.value}>
                    {k.value}
                  </Option>
                ))}
              </Select>
              <Select
                suffixIcon={SELECT_CARET}
                popupClassName="query-tool__dropdown"
                className="query-tool__field"
                placeholder="Condition"
                value={targetFilter.condition || undefined}
                onChange={handleTargetCondition}
                disabled={!targetFilter.category}
                popupMatchSelectWidth={false}
              >
                <Option value="IS">IS</Option>
                <Option value="IS NOT">IS NOT</Option>
                <Option value="LIKE">LIKE</Option>
                <Option value="NOT LIKE">NOT LIKE</Option>
              </Select>
              <Select
                suffixIcon={SELECT_CARET}
                popupClassName="query-tool__dropdown"
                className="query-tool__field"
                placeholder="Value"
                value={targetFilter.value || undefined}
                onChange={handleTargetValue}
                disabled={!targetFilter.category}
                popupMatchSelectWidth={false}
                loading={isTargetKeyFetching}
              >
                <Option value={NULL_VALUE}>NULL</Option>
                {(targetFeatures[targetFilter.category] ?? [])
                  .filter((v: any) => v !== undefined && v !== null)
                  .map((v: any) => (
                    <Option key={String(v)} value={v}>
                      {/* Keep raw `value` for query matching; format date timestamps for display. */}
                      {formatDisplayDate(v)}
                    </Option>
                  ))}
              </Select>
            </div>
          )}

          {submitError && <div className="query-tool__error query-tool__error--submit">{submitError}</div>}

          <div className="query-panel__footer">
            <button type="button" className="query-panel__results" disabled={isPending} onClick={handleApply}>
              {isPending ? <Spin size="small" /> : 'Get Results'}
            </button>
          </div>
        </div>
      )}

      {resultVisible && (
        <ViewAttributeTable
          currentMapId={mapId}
          isVisible={resultVisible}
          layerName={sourceLayer}
          layerTitle={layerTitleOf(sourceLayer)}
          seedSelectionIds={seedSelectionIds}
          seedMatchTotal={matchTotal}
          seedSelectAllMatches={seedSelectAllMatches}
          matchFilter={activeQuery?.filter}
          matchExtent={activeQuery?.extent}
          matchCql={activeQuery?.cql}
          proximityMatch={activeProximity}
          handleClose={() => {
            resetConfig();
            setMatchTotal(0);
            setResultVisible(false);
          }}
          isQueryTool
          onEditQuery={() => {
            setResultVisible(false);
            setProximityModalToolVisible(true);
            setState((prev) => ({
              ...prev,
              selectedFeatureIds: [],
              selectionCql: null,
              highlightColor: null,
            }));
          }}
          onResetQuery={handleReset}
          setIsLayerLoading={setIsLayerLoading}
          onLayerUpdateComplete={onLayerUpdateComplete}
          canEdit={canSaveResults}
        />
      )}
    </>
  );
};

export default ProximityModal;
